/**
 * TODO: REFACTOR NEEDED - surveyInterpreter.ts
 * 
 * CURRENT ISSUES:
 * 1. Massive code duplication - Same block processing logic repeated 3 times:
 *    - screen type (lines ~150-210)
 *    - landingPage type (lines ~235-450)
 *    - profile type (lines ~695-790)
 * 
 * 2. Inconsistent property names for images across blocks:
 *    - hero: block.media
 *    - feature: block.image
 *    - banner: block.logo
 *    - mediaGrid: items[].filename
 *    - card: block.media
 * 
 * 3. Missing abstraction - No unified image processing strategy
 * 
 * PROPOSED SOLUTION:
 * Create a single processBlock() function that:
 * - Detects and processes all image-like properties uniformly
 * - Handles nested items recursively
 * - Eliminates 200+ lines of duplicate code
 * - Automatically supports new block types
 * 
 * Example:
 * function processBlock(block: any): any {
 *   const processed = { ...block };
 *   
 *   // Unified image property detection
 *   const imageProps = ['image', 'media', 'logo'];
 *   for (const prop of imageProps) {
 *     if (block[prop]?.type === 'image' || block[prop]?.filename) {
 *       processed[prop] = processImage(block[prop], block[prop].aspectRatio);
 *     }
 *   }
 *   
 *   // Recursive item processing
 *   if (block.items) {
 *     processed.items = block.items.map(processBlock);
 *   }
 *   
 *   return processed;
 * }
 * 
 * This would replace all the duplicate if/else chains and make the code maintainable.
 */

import { filterSensitiveConfig } from './configFilter';
import translations from '$lib/translations/survey-player.yaml';
import type {
  Survey,
  SurveyScreen,
  BoxItem,
  ImageProviderConfig,
  Screen,
  LandingPage,
  ProfileBlock,
  ProfileScreen,
  UnprocessedImage,
  ProcessedImage as Image,
  AspectRatio,
  QuestionScreen,
  Options, 
  QuestionFooterData,
  QuestionComponentConfig,
  QuestionType
} from '@player/types/questions';
import type { ThemeConfig } from '@walku/core';
import { defaultTheme } from '@walku/core';
import type { ResultLogicConfig, ResultScreens, ResultBlock } from '@lib/components/screens/ResultsScreen';
import type { LogicConfig, LogicName, SkipLogicConfig, MultiAxisScoringConfig } from '@lib/logic/types';
import { getTranslations } from '../interpreter';
import type { SurveyConfig } from '../types/player';

// Define type for interpreted survey with required theme properties
export type InterpretedSurvey = Omit<Survey, 'theme'> & {
  theme: ThemeConfig;
  axes?: any[];
  profiles?: any[];
  config: SurveyConfig;
  id?: string; // Add id property for SurveyController
};

import type { ImageContext } from '../image/image-context';
import { IMAGE_CONTEXT_SIZES, IMAGE_CONTEXT_SIZES_ATTR, getDefaultSizeForContext, getImageSuffix } from '../image/image-context';

/**
 * Process video media, including poster image if present
 */
function processVideoMedia(video: any): any {
  if (!video || video.type !== 'video') return video;

  const processed = { ...video };

  // Process poster if it's an image object with filename
  if (video.poster && typeof video.poster === 'object' && video.poster.filename) {
    processed.poster = processImage(video.poster, {
      context: 'content',
      aspectRatio: video.poster.aspectRatio || video.aspectRatio
    });
  }

  return processed;
}

interface ProcessImageOptions {
  context?: ImageContext;
  aspectRatio?: AspectRatio;
  origin?: string; // For absolute URLs (OG images)
}

export function processImage(
  image: UnprocessedImage | undefined,
  options: ProcessImageOptions = {}
): Image | undefined {
  // Return undefined if no image is provided
  if (!image) {
    return undefined;
  }

  const { context = 'content', aspectRatio, origin } = options;

  // Start with all original image properties
  const processedImage = { ...image } as any;

  // If the image has SVG content, ensure it's preserved
  if (image.svg) {
    processedImage.aspectRatio = image.aspectRatio || aspectRatio;
    return processedImage;
  }

  // If the image has an animation, preserve it as-is
  if (image.animation) {
    processedImage.aspectRatio = image.aspectRatio || aspectRatio;
    return processedImage;
  }

  // If the image has a URL (hotlinked), preserve it as-is
  if (image.url) {
    // Preserve URL as-is for hotlinking (including Cloudinary or any other URLs)
    processedImage.aspectRatio = image.aspectRatio || aspectRatio;
    return processedImage;
  }

  // If no filename is provided at this point, throw an error
  if (!image.filename && !processedImage.filename) {
    throw new Error('Image must have either a filename or URL');
  }

  // Always use static images from /images/ directory
  const filename = processedImage.filename || image.filename;
  if (!filename) {
    throw new Error('Image processing error: no filename available');
  }

  // Clean the filename to remove any existing size suffixes
  const baseFilename = filename.replace(/_(w\d+|portrait|landscape)$/, '');

  // Get the default size for this context
  const defaultSize = getDefaultSizeForContext(context);
  const sizeSuffix = context === 'background' ? '' : getImageSuffix(defaultSize);

  // Build image path - all images are now in /images/ directly
  const imagePath = `/images/${baseFilename}${sizeSuffix}.webp`;

  // Generate responsive image sources based on context
  const contextSizes = IMAGE_CONTEXT_SIZES[context];
  const sources = contextSizes
    .slice()
    .reverse() // Smallest to largest for proper media query evaluation
    .map((size) => ({
      media: `(min-width: ${size}px)`,
      srcset: `/images/${baseFilename}${getImageSuffix(size)}.webp`
    }));

  // Update the processed image with computed values
  // Use absolute URL if origin is provided (for OG images)
  processedImage.url = origin ? `${origin}${imagePath}` : imagePath;
  processedImage.sources = sources;
  processedImage.sizes = IMAGE_CONTEXT_SIZES_ATTR[context]; // Use context-specific sizes attribute
  // Always preserve aspect ratio - original takes precedence, then passed parameter
  processedImage.aspectRatio = processedImage.aspectRatio || aspectRatio;
  processedImage.filename = baseFilename;
  // Add original URL as fallback (without size suffix)
  processedImage.originalSrc = origin ? `${origin}/images/${baseFilename}.webp` : `/images/${baseFilename}.webp`;

    return processedImage;
}

function processScreen(screen: SurveyScreen, language: string, commonElements?: Record<string, any>): SurveyScreen {
  if (screen.type === 'screen') {
    const processed = { ...screen } as Screen;
    
    // Check if screen has its own language property, preserve it
    if ((screen as any).language) {
      (processed as any).language = (screen as any).language;
    }
    
    // Process header
    if (screen.header) {
      processed.header = {
        title: screen.header.title,
        subtitle: screen.header.subtitle,
        image: screen.header.image ? processImage(screen.header.image, {
          context: 'content',
          aspectRatio: screen.header.image.aspectRatio
        }) : undefined
      };
    }

    // Process config - basic screen only
    processed.config = {
      ...screen.config,
      color: screen.config?.color,
      background: screen.config?.background ? {
        ...screen.config.background,
        image: screen.config.background.type === 'image' && screen.config.background.image ? processImage(screen.config.background.image, {
          context: 'background',
          aspectRatio: screen.config.background.image.aspectRatio
        }) : undefined,
        gradient: screen.config.background.type === 'gradient' && screen.config.background.gradient ? 
          screen.config.background.gradient : undefined
      } : undefined
    };

    // Process block images if present - basic text/image blocks only
    if (screen.blocks) {
      processed.blocks = screen.blocks.map((block: ProfileBlock) => {
        // Resolve common element references (dot notation: "common.mouiBanner")
        if (block.type === 'common' && (block as any).id) {
          if (commonElements) {
            const pathParts = ((block as any).id as string).split('.');
            let element: any = commonElements;
            for (const key of pathParts) {
              element = element?.[key];
            }
            if (element) {
              const { type, id, ...overrides } = block as any;
              block = { ...element, ...overrides } as ProfileBlock;
            } else {
              console.warn(`Common element not found: ${(block as any).id}`);
            }
          } else {
            console.warn(`Common elements not loaded, cannot resolve: ${(block as any).id}`);
          }
        }

        // Process block backgrounds if they have gradients or images
        const processedBlock = { ...block };
        if (block.background) {
          processedBlock.background = {
            ...block.background,
            image: block.background.type === 'image' && block.background.image ? processImage(block.background.image, {
              context: 'background',
              aspectRatio: block.background.image.aspectRatio
            }) : undefined,
            gradient: block.background.type === 'gradient' && block.background.gradient ? 
              block.background.gradient : undefined
          };
        }
        
        if (block.type === 'image' && block.image) {
          return {
            ...processedBlock,
            image: processImage(block.image, {
              context: 'content',
              aspectRatio: block.image.aspectRatio
            })
          };
        } else if (block.type === 'feature' && block.media?.type === 'image') {
          return {
            ...processedBlock,
            media: {
              ...block.media,
              ...processImage(block.media, {
                context: 'content',
                aspectRatio: block.media.aspectRatio
              })
            }
          };
        } else if (block.type === 'card') {
          // Process card block with media structure (like hero)
          // Handle media property
          if (block.media) {
            if (block.media.type === 'image' && (block.media.filename || block.media.url)) {
              processedBlock.media = {
                ...block.media,
                ...processImage(
                  {
                    filename: block.media.filename,
                    url: block.media.url,
                    alt: block.media.alt
                  },
                  {
                    context: 'card',
                    aspectRatio: block.media.aspectRatio
                  }
                )
              };
            } else if (block.media.type === 'video') {
              // Process video (handles poster image)
              processedBlock.media = processVideoMedia(block.media);
            } else {
              // For other types, pass through as-is
              processedBlock.media = block.media;
            }
          }

          return processedBlock;
        }
        return processedBlock;
      });
    }

    return processed;
  } else if (screen.type === 'landingPage') {
    const processed = { ...screen } as LandingPage;
    
    // Check if screen has its own language property, preserve it
    if ((screen as any).language) {
      (processed as any).language = (screen as any).language;
    }
    
    // Process header
    if (screen.header) {
      processed.header = {
        title: screen.header.title,
        subtitle: screen.header.subtitle,
        image: screen.header.image ? processImage(screen.header.image, {
          context: 'hero',
          aspectRatio: screen.header.image.aspectRatio
        }) : undefined
      };
    }

    // Process config and preserve all landing page properties
    processed.config = {
      ...screen.config,
      color: screen.config?.color,
      allowTextSelection: screen.config?.allowTextSelection,
      fullscreen: screen.config?.fullscreen,
      scrollable: screen.config?.scrollable,
      background: screen.config?.background ? {
        ...screen.config.background,
        image: screen.config.background.type === 'image' && screen.config.background.image ? processImage(screen.config.background.image, {
          context: 'background',
          aspectRatio: screen.config.background.image.aspectRatio
        }) : undefined,
        gradient: screen.config.background.type === 'gradient' && screen.config.background.gradient ? 
          screen.config.background.gradient : undefined
      } : undefined
    };

    // Process block images including hero and banner blocks
    if (screen.blocks) {
      processed.blocks = screen.blocks.map((block: ProfileBlock) => {
        // Resolve common element references (dot notation: "common.mouiBanner")
        if (block.type === 'common' && (block as any).id) {
          if (commonElements) {
            const pathParts = ((block as any).id as string).split('.');
            let element: any = commonElements;
            for (const key of pathParts) {
              element = element?.[key];
            }
            if (element) {
              const { type, id, ...overrides } = block as any;
              block = { ...element, ...overrides } as ProfileBlock;
            } else {
              console.warn(`Common element not found: ${(block as any).id}`);
            }
          } else {
            console.warn(`Common elements not loaded, cannot resolve: ${(block as any).id}`);
          }
        }

        if (block.type === 'image' && block.image) {
          return {
            ...block,
            image: processImage(block.image, {
              context: 'content',
              aspectRatio: block.image.aspectRatio
            })
          };
        } else if (block.type === 'hero') {
          // Process hero block with new media structure
          const processedBlock = { ...block };

          // Handle new media property
          if (block.media) {
            if (block.media.type === 'image' && (block.media.filename || block.media.url)) {
              processedBlock.media = {
                ...block.media,
                ...processImage(
                  {
                    filename: block.media.filename,
                    url: block.media.url,
                    alt: block.media.alt
                  },
                  block.media.aspectRatio,
                  false,
                  false,
                  undefined,
                  false,
                  'hero'  // Hero images need larger sizes
                )
              };
            } else if (block.media.type === 'video') {
              // Process video (handles poster image)
              processedBlock.media = processVideoMedia(block.media);
            } else {
              // For gradient and other types, pass through as-is
              processedBlock.media = block.media;
            }
          }
          // Handle legacy items array for backward compatibility
          else if (block.items) {
            processedBlock.items = block.items.map((item: any) => {
              if (item.type === 'image' && item.image) {
                return {
                  ...item,
                  image: processImage(item.image, {
                      context: 'card',
                      aspectRatio: item.aspectRatio || item.image.aspectRatio
                    })
                };
              }
              return item;
            });
          }

          // Preserve CSS property for responsive styles
          processedBlock.css = block.css;
          return processedBlock;
        } else if (block.type === 'banner' && block.logo?.type === 'image' && (block.logo.filename || (block.logo.src && !block.logo.src.startsWith('http')))) {
          // Process banner block logo images that use filenames
          const logoImage = {
            filename: block.logo.filename || block.logo.src,
            alt: block.logo.alt || '',
            aspectRatio: undefined
          };
          const processedImage = processImage(logoImage, { context: 'logo' });
          
          return {
            ...block,
            logo: {
              ...block.logo,
              src: processedImage?.url,
              sources: processedImage?.sources
            }
          };
        } else if (block.type === 'carousel' && block.items) {
          // Process carousel items recursively
          return {
            ...block,
            items: block.items.map((carouselItem: any) => {
              // If carousel item is a hero, process its media
              if (carouselItem.type === 'hero') {
                const processedHero = { ...carouselItem };

                // Handle new media property
                if (carouselItem.media) {
                  if (carouselItem.media.type === 'image' && (carouselItem.media.filename || carouselItem.media.url)) {
                    processedHero.media = {
                      ...carouselItem.media,
                      ...processImage({
                          filename: carouselItem.media.filename,
                          url: carouselItem.media.url,
                          alt: carouselItem.media.alt
                        }, {
                          context: 'content',
                          aspectRatio: carouselItem.media.aspectRatio
                        })
                    };
                  } else {
                    // For video and gradient, pass through as-is
                    processedHero.media = carouselItem.media;
                  }
                }
                // Handle legacy items array for backward compatibility
                else if (carouselItem.items) {
                  processedHero.items = carouselItem.items.map((item: any) => {
                    if (item.type === 'image' && item.image) {
                      return {
                        ...item,
                        image: processImage(item.image, {
                      context: 'card',
                      aspectRatio: item.aspectRatio || item.image.aspectRatio
                    })
                      };
                    }
                    return item;
                  });
                }

                return processedHero;
              }
              // If carousel item is a feature with image
              else if (carouselItem.type === 'feature' && carouselItem.image) {
                return {
                  ...carouselItem,
                  image: processImage(carouselItem.image, {
                    context: 'content',
                    aspectRatio: carouselItem.image.aspectRatio
                  })
                };
              }
              // If carousel item is an image
              else if (carouselItem.type === 'image' && carouselItem.image) {
                return {
                  ...carouselItem,
                  image: processImage(carouselItem.image, {
                    context: 'content',
                    aspectRatio: carouselItem.image.aspectRatio
                  })
                };
              }
              return carouselItem;
            })
          };
        } else if (block.type === 'mediaGrid' && block.items) {
          // Process mediaGrid items similar to carousel
          return {
            ...block,
            items: block.items.map((mediaItem: any) => {
              if (mediaItem.type === 'image' && (mediaItem.filename || mediaItem.url)) {
                return {
                  ...mediaItem,
                  ...processImage({
                      filename: mediaItem.filename,
                      url: mediaItem.url,
                      alt: mediaItem.alt
                    }, {
                      context: 'content',
                      aspectRatio: mediaItem.aspectRatio
                    })
                };
              }
              // For video items, pass through as-is
              return mediaItem;
            })
          };
        } else if (block.type === 'feature' && block.media) {
          if (block.media.type === 'image') {
            return {
              ...block,
              media: {
                ...block.media,
                ...processImage(block.media, {
                  context: 'feature',
                  aspectRatio: block.media.aspectRatio
                })
              }
            };
          } else if (block.media.type === 'video') {
            return {
              ...block,
              media: processVideoMedia(block.media)
            };
          }
          return block;
        } else if (block.type === 'card') {
          // Process card block with media structure (like hero)
          const processedBlock = { ...block };
          
          // Handle media property
          if (block.media) {
            if (block.media.type === 'image' && (block.media.filename || block.media.url)) {
              processedBlock.media = {
                ...block.media,
                ...processImage(
                  {
                    filename: block.media.filename,
                    url: block.media.url,
                    alt: block.media.alt
                  },
                  block.media.aspectRatio,
                  false,
                  false,
                  undefined,
                  false,
                  'hero'  // Hero images need larger sizes
                )
              };
            } else if (block.media.type === 'video') {
              // Process video (handles poster image)
              processedBlock.media = processVideoMedia(block.media);
            } else {
              // For other types, pass through as-is
              processedBlock.media = block.media;
            }
          }

          // Handle background property
          if (block.background) {
            if (block.background.type === 'image' && (block.background.filename || block.background.url)) {
              processedBlock.background = {
                ...block.background,
                ...processImage({
                    filename: block.background.filename,
                    url: block.background.url,
                    alt: block.background.alt
                  }, {
                    context: 'background',
                    aspectRatio: block.background.aspectRatio
                  })
              };
            } else {
              // For gradient, pass through as-is
              processedBlock.background = block.background;
            }
          }


          return processedBlock;
        } else if (block.type === 'blogArchive') {
          // Preserve all properties for blogArchive blocks (posts, parentRoute injected by server)
          return { ...block };
        }
        return block;
      });
    }

    // Also process logic for screens
    if (screen.logic) {
      processed.logic = screen.logic;
    }

    return processed;
  }

  if (screen.type === 'submit') {
    const processed = { ...screen };

    // Process header for submit screens
    if (screen.header) {
      // For submit screens, preserve the entire header structure including animations
      processed.header = {
        title: screen.header.title,
        subtitle: screen.header.subtitle,
        image: screen.header.image ? {
          ...processImage(screen.header.image, {
            context: 'hero',
            aspectRatio: screen.header.image.aspectRatio
          }),
          // Preserve animation property if it exists
          animation: screen.header.image.animation
        } : undefined
      };
    }

    // Process config for submit screens
    if (screen.config) {
      processed.config = { ...screen.config };
    }

    return processed;
  }

  if (screen.type === 'question') {
    const processed = { ...screen };

    // Check if question has its own language property, otherwise use global language
    const questionLanguage = (screen as any).language || language;

    // Preserve the language property if it exists
    if ((screen as any).language) {
      (processed as any).language = (screen as any).language;
    }

    // Get translations for the question-specific language
    const screenTranslations = getTranslations(questionLanguage);
    processed.skipText = screenTranslations.skipText;
    processed.submitText = screenTranslations.submitText;
    processed.retryText = screenTranslations.retryText;
    processed.continueText = screenTranslations.continueText;

    // Process header
    if (screen.header) {
      // Create the base header object first
      processed.header = {
        title: screen.header.title, 
        subtitle: screen.header.subtitle,
        image: screen.header.image ? processImage(screen.header.image, {
          context: 'content',
          aspectRatio: screen.header.image.aspectRatio
        }) : undefined,
      };
      // Now, add the blocks property IF it exists on the source header
      if (screen.header.blocks) {
         // @ts-expect-error - Acknowledging potential type mismatch, forcing assignment
        processed.header.blocks = screen.header.blocks;
      }
    }

    // Pass through weights configuration directly if present
    if (screen.weights) {
      processed.weights = screen.weights;
    }

    // Process logic configuration and result screens
    if (screen.logic) {
      processed.logic = screen.logic.map((logic: any) => {
        if (logic.name === 'checkAnswer' && logic.config && 'resultScreens' in logic.config) {
          const resultConfig = logic.config;
          const correctAnswer = resultConfig.correctAnswer;
          const { incorrect, correct } = resultConfig.resultScreens;

          // Process result screens headers and images
          const processedResultScreens: ResultScreens = {
            incorrect: {
              header: incorrect.header ? {
                title: incorrect.header.title,
                subtitle: incorrect.header.subtitle,
                image: incorrect.header.image ? 
                  processImage(incorrect.header.image, {
                      context: 'content',
                      aspectRatio: incorrect.header.image.aspectRatio
                    }) : undefined
              } : undefined,
              blocks: incorrect.blocks
            }
          };

          // Only add correct screen if it exists
          if (correct?.header) {
            processedResultScreens.correct = {
              header: {
                title: correct.header.title,
                subtitle: correct.header.subtitle,
                image: correct.header.image ? 
                  processImage(correct.header.image, {
                      context: 'content',
                      aspectRatio: correct.header.image.aspectRatio
                    }) : undefined
              },
              blocks: correct.blocks
            };
          }

          return {
            ...logic,
            config: {
              ...resultConfig,
              correctAnswer: correctAnswer,
              resultScreens: processedResultScreens
            } as ResultLogicConfig
          };
        }
        return logic;
      });
    }

    // Process boxes images if present
    if (screen.questionType === 'boxes' && screen.options) {
      const boxesOptions = screen.options || {};
      const isPortrait = screen.config?.layout === 'portrait';
      
      // Process items to match multitext structure but keep image processing
      // Don't force aspect ratios - let each image use its own
      const processedItems = boxesOptions.items?.map((item: any) => ({
        ...item,
        label: item.label || '',
        image: item.image ? processImage(item.image, {
          context: 'question',
          aspectRatio: item.image.aspectRatio
        }) : undefined
      })) || [];

      // Process altItems if they exist
      const processedAltItems = boxesOptions.altItems?.map((item: any) => ({
        ...item,
        label: item.label || '',
        image: item.image ? processImage(item.image, {
          context: 'question',
          aspectRatio: item.image?.aspectRatio
        }) : undefined
      }));

      processed.options = {
        items: processedItems,
        altItems: processedAltItems,
        altResponse: boxesOptions.altResponse
      };

      // Use config from root level only
      processed.config = screen.config;
    }

    // Process Likert options if present
    if (screen.questionType === 'likert') {
      const likertOptions = screen.options || {};
      processed.options = {
        items: likertOptions.items || [],
        altResponse: likertOptions.altResponse
      };
      // Use config from root level only
      processed.config = screen.config;
    } else if (screen.questionType === 'emojiLikert') {
      const emojiOptions = screen.options || {};
      processed.options = {
        items: emojiOptions.items || [],
        altResponse: emojiOptions.altResponse
      };
      // Use config from root level only
      processed.config = screen.config;
    } else if (screen.questionType === 'starRating') {
      const starOptions = screen.options || {};
      processed.options = {
        altResponse: starOptions.altResponse
      };
      // Use config from root level only
      processed.config = screen.config;
    } else if (screen.questionType === 'nps') {
      const npsOptions = screen.options || {};
      processed.options = {
        altResponse: npsOptions.altResponse
      };
      // Use config from root level only
      processed.config = screen.config;
    } else if (screen.questionType === 'multitext') {
      const multitextOptions = screen.options || {};
      processed.options = {
        items: multitextOptions.items || [],
        altItems: multitextOptions.altItems,
        altResponse: multitextOptions.altResponse
      };
      // Use config from root level only
      processed.config = screen.config;
    }

    // Also process logic for screens
    if (screen.logic) {
      processed.logic = screen.logic;
    }

    return processed;
  }

  if (screen.type === 'profile') {
    const processed = { ...screen } as ProfileScreen;

    // Process header
    if (screen.header) {
      processed.header = {
        title: screen.header.title,
        subtitle: screen.header.subtitle,
        image: screen.header.image ? processImage(screen.header.image, {
          context: 'hero',
          aspectRatio: screen.header.image.aspectRatio
        }) : undefined
      };
    }

    // Process background if present
    if (screen.config?.background?.type === 'image' && screen.config.background.image) {
      processed.config = {
        ...screen.config,
        background: {
          ...screen.config.background,
          image: processImage(screen.config.background.image, {
            context: 'background',
            aspectRatio: screen.config.background.image.aspectRatio
          })
        }
      };
    }

    // Process block images if present
    if (screen.blocks) {
      processed.blocks = screen.blocks.map((block: ProfileBlock) => {
        // Resolve common element references (dot notation: "common.mouiBanner")
        if (block.type === 'common' && (block as any).id) {
          if (commonElements) {
            const pathParts = ((block as any).id as string).split('.');
            let element: any = commonElements;
            for (const key of pathParts) {
              element = element?.[key];
            }
            if (element) {
              const { type, id, ...overrides } = block as any;
              block = { ...element, ...overrides } as ProfileBlock;
            } else {
              console.warn(`Common element not found: ${(block as any).id}`);
            }
          } else {
            console.warn(`Common elements not loaded, cannot resolve: ${(block as any).id}`);
          }
        }

        if (block.type === 'image' && block.image) {
          return {
            ...block,
            image: processImage(block.image, {
              context: 'content',
              aspectRatio: block.image.aspectRatio
            })
          };
        } else if (block.type === 'hero') {
          // Process hero block with new media structure
          const processedBlock = { ...block };

          // Handle new media property
          if (block.media) {
            if (block.media.type === 'image' && (block.media.filename || block.media.url)) {
              processedBlock.media = {
                ...block.media,
                ...processImage(
                  {
                    filename: block.media.filename,
                    url: block.media.url,
                    alt: block.media.alt
                  },
                  block.media.aspectRatio,
                  false,
                  false,
                  undefined,
                  false,
                  'hero'  // Hero images need larger sizes
                )
              };
            } else if (block.media.type === 'video') {
              // Process video (handles poster image)
              processedBlock.media = processVideoMedia(block.media);
            } else {
              // For gradient and other types, pass through as-is
              processedBlock.media = block.media;
            }
          }
          // Handle legacy items array for backward compatibility
          else if (block.items) {
            processedBlock.items = block.items.map((item: any) => {
              if (item.type === 'image' && item.image) {
                return {
                  ...item,
                  image: processImage(item.image, {
                      context: 'card',
                      aspectRatio: item.aspectRatio || item.image.aspectRatio
                    })
                };
              }
              return item;
            });
          }

          // Preserve CSS property for responsive styles
          processedBlock.css = block.css;
          return processedBlock;
        } else if (block.type === 'banner' && block.logo?.type === 'image' && (block.logo.filename || (block.logo.src && !block.logo.src.startsWith('http')))) {
          // Process banner block logo images that use filenames
          const logoImage = {
            filename: block.logo.filename || block.logo.src,
            alt: block.logo.alt || '',
            aspectRatio: undefined
          };
          const processedImage = processImage(logoImage, { context: 'logo' });
          
          return {
            ...block,
            logo: {
              ...block.logo,
              src: processedImage?.url,
              sources: processedImage?.sources
            }
          };
        } else if (block.type === 'card') {
          // Process card block with media structure (like hero)
          const processedBlock = { ...block };
          
          // Handle media property
          if (block.media) {
            if (block.media.type === 'image' && (block.media.filename || block.media.url)) {
              processedBlock.media = {
                ...block.media,
                ...processImage(
                  {
                    filename: block.media.filename,
                    url: block.media.url,
                    alt: block.media.alt
                  },
                  block.media.aspectRatio,
                  false,
                  false,
                  undefined,
                  false,
                  'hero'  // Hero images need larger sizes
                )
              };
            } else if (block.media.type === 'video') {
              // Process video (handles poster image)
              processedBlock.media = processVideoMedia(block.media);
            } else {
              // For other types, pass through as-is
              processedBlock.media = block.media;
            }
          }

          // Handle background property
          if (block.background) {
            if (block.background.type === 'image' && (block.background.filename || block.background.url)) {
              processedBlock.background = {
                ...block.background,
                ...processImage({
                    filename: block.background.filename,
                    url: block.background.url,
                    alt: block.background.alt
                  }, {
                    context: 'background',
                    aspectRatio: block.background.aspectRatio
                  })
              };
            } else {
              // For gradient, pass through as-is
              processedBlock.background = block.background;
            }
          }


          return processedBlock;
        } else if (block.type === 'blogArchive') {
          // Preserve all properties for blogArchive blocks (posts, parentRoute injected by server)
          return { ...block };
        }
        return block;
      });
    }

    // Also process logic for screens
    if (screen.logic) {
      processed.logic = screen.logic;
    }

    return processed;
  }

  return screen;
}

export function interpretSurvey(survey: Survey, origin?: string): InterpretedSurvey {
  if (!survey) {
    throw new Error('Survey data is required');
  }

  if (!survey.config) {
    throw new Error('Survey config is required');
  }

  if (!survey.screens) {
    throw new Error('Survey screens are required');
  }

  if (!survey.config.deployment?.domain) {
    throw new Error('Survey deployment.domain is required');
  }

  // Create a typed copy we can safely modify
  const processedSurvey = { ...survey } as any;
  const language = survey.config.language;

  // Always use the deployment domain for absolute URLs
  const domainOrigin = `https://${survey.config.deployment.domain}`;

  if (!language) {
    throw new Error('Survey language is required in config');
  }

  const supportedLanguages = Object.keys(translations);
  if (!supportedLanguages.includes(language)) {
    throw new Error(
      `Unsupported survey language '${language}'. Supported: ${supportedLanguages.join(', ')}`
    );
  }

  // Check for required database and collection fields
  if (!survey.config.database) {
    throw new Error('Survey database is required in config');
  }

  if (!survey.config.collection) {
    throw new Error('Survey collection is required in config');
  }

  // Construct the survey ID from database/collection for validation
  const constructedId = `${survey.config.database}/${survey.config.collection}`;

  // Filter sensitive config data (removes dashboard.accessToken, api credentials)
  // All other config properties (feature flags, metadata, etc.) are preserved automatically
  processedSurvey.config = filterSensitiveConfig(survey.config);

  const typedSurvey = survey as any;

  // Apply theme with defaults
  // First priority: check if there's a top-level theme property
  if (typedSurvey.theme && typeof typedSurvey.theme === 'object') {
    const customTheme = typedSurvey.theme;
    processedSurvey.theme = {
      ...defaultTheme,
      ...customTheme,
      // Preserve specific theme properties that might be overwritten
      primaryColor: customTheme.primaryColor || defaultTheme.primaryColor,
      secondaryColor: customTheme.secondaryColor || defaultTheme.secondaryColor,
      tertiaryColor: customTheme.tertiaryColor || defaultTheme.tertiaryColor,
      successColor: customTheme.successColor || defaultTheme.successColor,
      dangerColor: customTheme.dangerColor || defaultTheme.dangerColor,
      warningColor: customTheme.warningColor || defaultTheme.warningColor,
      textColor: customTheme.textColor || defaultTheme.textColor,
      textColorDark: customTheme.textColorDark || defaultTheme.textColorDark,
      textOnPrimary: customTheme.textOnPrimary || defaultTheme.textOnPrimary,
      textOnSecondary: customTheme.textOnSecondary || defaultTheme.textOnSecondary,
      textOnTertiary: customTheme.textOnTertiary || defaultTheme.textOnTertiary,
      // Preserve theme mode if it exists
      theme: customTheme.theme || defaultTheme.theme
    } as ThemeConfig;
  }
  // Second priority: fallback to default theme
  else {
    processedSurvey.theme = { ...defaultTheme } as ThemeConfig;
  }

  // Pass through axes and profiles if present in the survey
  if (typedSurvey.axes) {
    processedSurvey.axes = typedSurvey.axes;
  }

  if (typedSurvey.profiles) {
    // Process profile images (header, og, and blocks for landingPage profiles)
    processedSurvey.profiles = typedSurvey.profiles.map((profile: any) => {
      const processed = { ...profile };

      // Process header image for type: profile
      if (profile.header?.image) {
        processed.header = {
          ...profile.header,
          image: processImage(profile.header.image, {
              context: 'content',
              aspectRatio: profile.header.image.aspectRatio || '1:1',
              origin: domainOrigin
            })
        };
      }

      // Process og image (required for landingPage, optional for profile)
      if (profile.og?.image) {
        processed.og = {
          ...profile.og,
          image: processImage(profile.og.image, {
              context: 'content',
              aspectRatio: profile.og.image.aspectRatio || '1:1',
              origin: domainOrigin
            })
        };
      }

      // Process config background image for profile
      if (profile.config?.background?.type === 'image' && profile.config.background.image) {
        processed.config = {
          ...profile.config,
          background: {
            ...profile.config.background,
            image: processImage(profile.config.background.image, {
              context: 'background',
              aspectRatio: profile.config.background.image.aspectRatio
            })
          }
        };
      }

      // Process blocks for landingPage profiles (same logic as screen blocks)
      if (profile.blocks && Array.isArray(profile.blocks)) {
        processed.blocks = profile.blocks.map((block: any) => {
          const processedBlock = { ...block };

          if (block.type === 'image' && block.image) {
            processedBlock.image = processImage(block.image, {
              context: 'content',
              aspectRatio: block.image.aspectRatio
            });
          } else if ((block.type === 'card' || block.type === 'hero' || block.type === 'feature') && block.media) {
            if (block.media.type === 'image' && (block.media.filename || block.media.url)) {
              processedBlock.media = {
                ...block.media,
                ...processImage(
                  {
                    filename: block.media.filename,
                    url: block.media.url,
                    alt: block.media.alt
                  },
                  {
                    context: block.type === 'hero' ? 'hero' : block.type === 'card' ? 'card' : 'content',
                    aspectRatio: block.media.aspectRatio
                  }
                )
              };
            }
          }

          return processedBlock;
        });
      }

      return processed;
    });
  }

  // Process OG image if present
  if (processedSurvey.config?.og?.image) {
    processedSurvey.config.og = {
      ...processedSurvey.config.og,
      image: processImage(processedSurvey.config.og.image, {
        context: 'content',
        aspectRatio: processedSurvey.config.og.image.aspectRatio || '1:1',
        origin: domainOrigin
      })
    };
  }

  // Process screens with the image configuration
  const commonElements = (typedSurvey as any).commonElements;
  return {
    ...processedSurvey,
    screens: processedSurvey.screens.map((screen: SurveyScreen) =>
      processScreen(screen, language, commonElements)
    )
  } as InterpretedSurvey;
} 